import React from "react";
import FadeReveal from "./FadeReveal";
import {
  SiReact,
  SiNodedotjs,
  SiJavascript,
  SiTypescript,
  SiPython,
  SiDjango,
  SiC,
  SiMysql,
  SiGit,
  SiSupabase,
  SiHtml5,
  SiCss,
} from "react-icons/si";
import { MdSensors } from "react-icons/md";
import { FaAws } from "react-icons/fa";

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M8 11L4 7h2.5V2h3v5H12L8 11z" fill="currentColor" />
    <path d="M2 13h12v1H2v-1z" fill="currentColor" />
  </svg>
);

const GitHubIcon = () => (
  <svg viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path fillRule="evenodd" d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z" />
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const EmailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <path d="M22 6l-10 7L2 6" />
  </svg>
);

const skills = [
  { name: "React",      Icon: SiReact,      color: "#61DAFB" },
  { name: "Node.js",    Icon: SiNodedotjs,  color: "#339933" },
  { name: "JavaScript", Icon: SiJavascript, color: "#e0a010" },
  { name: "TypeScript", Icon: SiTypescript, color: "#3178C6" },
  { name: "Python",     Icon: SiPython,     color: "#3776AB" },
  { name: "Django",     Icon: SiDjango,     color: "#0c5c3f" },
  { name: "C",          Icon: SiC,          color: "#5c6b7a" },
  { name: "SQL",        Icon: SiMysql,      color: "#4479A1" },
  { name: "Git",        Icon: SiGit,        color: "#F05032" },
  { name: "Supabase",   Icon: SiSupabase,   color: "#3ECF8E" },
  { name: "HTML5",      Icon: SiHtml5,      color: "#E34F26" },
  { name: "CSS3",       Icon: SiCss,        color: "#1572B6" },
  { name: "AWS",        Icon: FaAws,        color: "#d97706" },
  { name: "IoT",        Icon: MdSensors,    color: "#0891b2" },
];

// Diagonal marquee rows drift in alternating directions behind the hero.
// Each row is offset differently so the diagonal reads as varied, not tiled.
// Bump ROW_COUNT to add or remove rows.
const ROW_COUNT = 16;
const marqueeRows = Array.from({ length: ROW_COUNT }, (_, i) => {
  const offset = (i * 3) % skills.length; // stagger each row's starting skill
  return [...skills.slice(offset), ...skills.slice(0, offset), ...skills.slice(0, 6)];
});

const DiagonalMarquee = () => (
  <div className="about-marquee" aria-hidden="true">
    <div className="about-marquee__stage">
      {marqueeRows.map((row, i) => (
        <div
          key={i}
          className={`about-marquee__row ${i % 2 === 0 ? "drift-left" : "drift-right"}`}
        >
          <div className="about-marquee__track">
            {row.concat(row).map((skill, j) => (
              <span className="about-marquee__item" key={`${skill.name}-${j}`}>
                <skill.Icon size={20} />
                {skill.name}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const About = () => {
  return (
    <section id="home">
      <DiagonalMarquee />
      <div className="hero">
        <FadeReveal direction="up" threshold={0} rootMargin="0px">
          <div className="hero-top">
            <span className="eyebrow">Portfolio — Laur-Alexandru Cristea</span>
            <span className="hero-avail">
              <span className="dot" aria-hidden="true"></span>
              Available for junior roles
            </span>
          </div>

          <h1 className="hero-display">
            Computer science<br />
            student building <span className="em">premium</span><br />
            <span className="thin">web experiences.</span>
          </h1>
        </FadeReveal>

        <div className="hero-lower">
          <FadeReveal direction="up" delay={0.1} threshold={0} rootMargin="0px">
            <div>
              <p className="hero-body">
                I'm <strong>Alexandru</strong>, a third-year CS student in Timișoara who
                cares about the details most people skip. I build fast, thoughtful
                interfaces and full-stack systems.
              </p>

              <div className="hero-actions">
                <a href="#projects" className="btn btn--solid">View selected work</a>
                <a href="/Cristea_Laur-Alexandru_CV.pdf" download className="btn btn--ghost">
                  <DownloadIcon /> Resume
                </a>
              </div>

              <div className="social-row">
                <a href="https://github.com/AlexandruCL" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="GitHub profile" style={{ "--hover-color": "var(--ink)" }}>
                  <GitHubIcon />
                </a>
                <a href="https://www.linkedin.com/in/laur-alexandru-cristea-24b867312/" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="LinkedIn profile" style={{ "--hover-color": "#0a66c2" }}>
                  <LinkedInIcon />
                </a>
                <a href="mailto:alex.cristea.laur2004@gmail.com" className="social-link" aria-label="Send an email" style={{ "--hover-color": "#ea4335" }}>
                  <EmailIcon />
                </a>
              </div>
            </div>
          </FadeReveal>

          <FadeReveal direction="up" delay={0.18} threshold={0} rootMargin="0px">
            <div className="hero-meta">
              <div className="hero-meta-row">
                <span className="k">Role</span>
                <span className="v">CS Student &amp; Web Developer</span>
              </div>
              <div className="hero-meta-row">
                <span className="k">Based in</span>
                <span className="v">Timișoara, Romania</span>
              </div>
              <div className="hero-meta-row">
                <span className="k">Focus</span>
                <span className="v">Full-stack · Cloud </span>
              </div>
              <div className="hero-meta-row" style={{ borderBottom: "none" }}>
                <span className="k">Currently</span>
                <span className="v">Learning React &amp; Node.js</span>
              </div>
            </div>
          </FadeReveal>
        </div>

        <FadeReveal direction="up" delay={0.1}>
          <div className="skills-band">
            <p className="band-label">Toolbox</p>
            <ul className="skills-list">
              {skills.map((skill) => (
                <li key={skill.name}>
                  <span className="skill-chip" style={{ "--hover-color": skill.color }}>
                    <skill.Icon size={16} />
                    {skill.name}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </FadeReveal>
      </div>
    </section>
  );
};

export default About;
